import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Alert, DatePicker, Form, Input, Space} from 'antd';
import {connect} from 'react-redux';
import dayjs from 'dayjs';
import {
  StyledInvoiceDetailsCard,
  StyledInvoiceDetailsCardRadioGroup,
  StyledInvoiceDetailsCardVendorSpace,
  StyledInvoiceDetailsCardTable
} from './StyledInvoiceDetailsCard';
import VendorSelectorWithAuth from '../../TransactionsPage/VendorSelectorWithAuth';
import {invoicesHelpers, transactionsHelpers, vendorsHelpers} from '../../../../utils/helpers';
import {invoicePaymentStatusConstants} from '../../../../constants';
import {vendorsActions} from '../../../../state/actions/vendors.actions';

const initialFormValues = {
  amount: '',
  date: undefined,
  due_date: undefined,
  paid_date: undefined,
  recipient: '',
  type: undefined,
  vendor_local_id: '',
}

const {Item} = Form;

const formId = 'invoice-details-form';

const getDateValue = (value) => value ? value.format('YYYY-MM-DD') : undefined;
const formatDate = (date) => (date ? dayjs(date) : undefined);

const InvoiceDetailsCard = ({
  invoice,
  isVendorEditDisabled = false,
  onEdit,

  dispatch,
  getVendor,
  employees,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);
  const [vendor, setVendor] = useState(null);

  const [form] = Form.useForm();

  const amount = Form.useWatch('amount', form);

  const amountInputRef = useRef(null);
  const amountGhostTextRef = useRef(null);

  const isAlreadyPaid = useMemo(() => invoice?.payment_status === invoicePaymentStatusConstants.PAID, [invoice]);

  const uploadedBy = useMemo(() => {
    const email = invoice?.uploaded_by;
    const employee = employees.find(employee => employee.email === email);
    return employee ? `${employee.full_name} <${email}>` : email || '';
  }, [employees, invoice]);

  const documentTypeOptions = useMemo(() => transactionsHelpers.getInvoiceTypeOptions({t}), [t]);

  const invalidVendor = useMemo(() => vendorsHelpers.isVendorToBeValidated(vendor), [vendor]);

  const loadVendorData = (invoice) => {
    const id = invoice?.vendor_local_id;
    if (!id) {
      if (vendor) setVendor(null);
      return;
    }

    getVendor({id, successCallback: setVendor});
  }

  useEffect(() => {
    if (!invoice) return;

    const { amount, date, due_date, paid_date, recipient, type, vendor_local_id } = invoice;

    form.setFieldsValue({
      amount,
      date: formatDate(date),
      due_date: formatDate(due_date),
      paid_date: formatDate(paid_date),
      recipient,
      type,
      vendor_local_id
    });
  }, [form, invoice]);

  useEffect(() => {
    loadVendorData(invoice);
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (amountGhostTextRef.current && amountInputRef.current) {
      amountInputRef.current.input.style.width = `${(amountGhostTextRef?.current?.offsetWidth || 0) + 4}px`;
    }
  }, [amount]);

  const requiredRules = [{required: true, message: t('validation.fieldIsRequired')}];

  const handleSubmitForm = () => form.submit();

  const handlePressEnter = () => {
    handleSubmitForm();
    document.querySelectorAll(`#${formId} input`).forEach((input) => input.blur());
  }

  const handleEditVendorSuccess = () => {
    if (!invalidVendor) return;

    loadVendorData(invoice);
  }

  const formElementProps = {
    onBlur: handleSubmitForm,
    onPressEnter: handlePressEnter
  }

  const getDateField = (name, pickerProps) => (
    <Item
      name={name}
      rules={requiredRules}
    >
      <DatePicker
        format='DD MMM YYYY'
        style={{width: '100%'}}
        {...formElementProps}
        {...pickerProps}
      />
    </Item>
  );

  const vendorField = (
    <StyledInvoiceDetailsCardVendorSpace
      align='center'
      size='small'
      wrap
    >
      {invalidVendor && <Alert message={`${t('add')} ${t('vat')}`} type='warning' showIcon />}
      <Item
        className='invoice-vendor-select'
        name='vendor_local_id'
      >
        <VendorSelectorWithAuth
          selectProps={{
            customSelectedRenderer: (props) =>
              vendorsHelpers.getVendorNameWithLink({
                vendor_id: props.value,
                name: props.name
              }),
            disabled: isVendorEditDisabled,
            isEnableAdd: false,
            isEnableAddVat: invalidVendor,
            showArrow: false,
          }}
          onChange={handleSubmitForm}
          onSubmitSuccess={handleEditVendorSuccess}
          reloadOnMissingValue={invalidVendor}
          showBeneficiaryInfo={false}
        />
      </Item>
    </StyledInvoiceDetailsCardVendorSpace>
  );

  const amountField = (
    <div className='amount-input'>
      <span
        ref={amountGhostTextRef}
        className='amount-ghost-test'
      >
        {amount || ' '}
      </span>
      <Item
        name='amount'
        rules={requiredRules}
      >
        <Input
          ref={amountInputRef}
          prefix='€'
          min={0.01}
          step='any'
          type='number'
          {...formElementProps}
        />
      </Item>
    </div>
  );

  const documentTypeField = (
    <Item
      name='type'
    >
      <StyledInvoiceDetailsCardRadioGroup
        onChange={handleSubmitForm}
        options={documentTypeOptions}
      />
    </Item>
  );

  const issuedToField = (
    <Item name='recipient'>
      <Input {...formElementProps} />
    </Item>
  );

  const details = [
    {
      key: 'vendor',
      label: t('vendor'),
      value: vendorField
    },
    {
      key: 'issued_to',
      label: t('invoices:issuedTo'),
      value: issuedToField
    },
    {
      key: 'amount',
      label: t('amount'),
      value: amountField
    },
    {
      key: 'invoice-date',
      label: `${t('invoice')} ${t('date')}`,
      value: getDateField('date')
    },
    {
      key: 'due_date',
      label: `${t('due')} ${t('date')}`,
      value: getDateField('due_date')
    },
    isAlreadyPaid && {
      key: 'paid_date',
      label: `${t('payment')} ${t('date')}`,
      value: getDateField('paid_date', {disabled: true})
    },
    {
      key: 'uploaded_by',
      label: t('invoices:uploadedBy'),
      value: uploadedBy
    },
    {
      key: 'type',
      label: `${t('document')} ${t('type')}`,
      value: documentTypeField
    }
  ].filter(Boolean);

  const handleSubmit = (fields) => {
    let data = {
      amount: fields.amount,
      date: getDateValue(fields.date),
      due_date: getDateValue(fields.due_date),
      recipient: fields.recipient || '',
      type: fields.type,
    }
    if (!isVendorEditDisabled) {
      data = {...data, vendor_local_id: fields.vendor_local_id };
    }
    onEdit?.({data});
  }

  const items = [
    {
      key: '1',
      label: (
        <Space align='center' size='middle'>
          <span>{t('invoice')} {t('details')}</span>
          {invoicesHelpers.getInvoiceSourceTypeTag(invoice?.source_type)}
        </Space>
      ),
      children: (
        <Form
          id={formId}
          initialValues={initialFormValues}
          form={form}
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <StyledInvoiceDetailsCardTable data={details} />
        </Form>
      ),
      extra: invoicesHelpers.getInvoiceStatusMark(invoice?.payment_status)
    }
  ]

  return (
    <StyledInvoiceDetailsCard
      {...rest}
      items={items}
      defaultActiveKey={['1']}
    />
  );
}

InvoiceDetailsCard.propTypes = {
  invoice: PropTypes.shape({
    amount: PropTypes.number,
    date: PropTypes.string,
    due_date: PropTypes.string,
    payment_status: PropTypes.string,
    recipient: PropTypes.string,
    source_type: PropTypes.string,
    vendor: PropTypes.string,
    uploaded_by: PropTypes.string,
  }),
  isVendorEditDisabled: PropTypes.bool,
  onEdit: PropTypes.func,
}

const mapStateToProps = state => ({
  employees: state.company.employees
});

const mapDispatchToProps = {
  getVendor: vendorsActions.getVendor,
}

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailsCard);
